import React, { useEffect, useRef, useState } from 'react';
import styles from './graph-container.module.scss';
import {
  BatteryChart,
  NewAreaChart,
  Stackbar,
} from '@flexo/organisms';

import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { ChartSkeleton, Shimmer } from '@flexo/atoms';
import { useKpiData } from '@flexo/hooks';
const ENV = (import.meta as any).env;

const GraphContainer = React.memo((props: any) => {
  const metadata = useSelector((state: any) => state.metadata);
  const kpi = useSelector((state: any) => state.kpi);
  const location = useLocation();
  const pathSegment = location.pathname.split('/')[1];

  const ThemeStore = useSelector((state: any) => state.theme);
  const [graphValues, setGraphValues] = useState([]);

  let entity = 'Site';
  let kpiContext = 'Energy';

  if (pathSegment?.includes('economics')) {
    kpiContext = 'Economics';
  }

  if (pathSegment?.includes('community')) {
    entity = 'Community';
  }
  if (pathSegment?.includes('member')) {
    entity = 'Member';
  }

  if (props?.kpiContext === 'economics') {
    kpiContext = 'Economics';
  }
  if (props?.kpiContext === 'ancillary') {
    kpiContext = 'Ancillary';
  }

  let detailKpiData: any = kpi[`${kpiContext}${entity}`]?.['1hour'] || null;
  const overviewKpiData = kpi[`${kpiContext}${entity}`]?.['1day'] || null;
  const LOADING_STATE = kpi?.[`${kpiContext}KpiData${entity}AsyncStatus`];

  // const LOADING_STATE = 'PENDING';
  
  // Use the new hook for KPI data access
  const { findApiUnit, findApiValue } = useKpiData(detailKpiData?.[0]?.kpi, kpiContext.toLowerCase());

  useEffect(() => {
    let filteredKeys = props?.graphKeys?.filter((el) =>
      detailKpiData?.[0]?.kpi.some(
        (kpiItem) => kpiItem.name === (typeof el === 'string' ? el : el?.key)
      )
    );

    if (kpiContext === 'Economics') {
      const availableData = (overviewKpiData?.[0]?.kpi || [])?.filter(datum => datum.value !== null && datum.value !== '' && datum.value !== undefined);
      
      filteredKeys = filteredKeys?.filter(el => availableData.some(kpi => kpi.name === (typeof el === 'string' ? el : el?.key)));
    }

    setGraphValues(filteredKeys);
  }, [props.graphKeys, detailKpiData]);

  const getTranformDataAreachart = (data, graphKeys) => {
    return data?.map((entry) => {
      const kpiMap = {};
      entry?.kpi?.forEach((kpiItem) => {
        graphKeys?.forEach((key) => {
          if (kpiItem.name === key) {
            const formattedKey = `widgets.${props.subtype}.${key}`;
            kpiMap[formattedKey] = kpiItem.value?.value || kpiItem.value;
          }
        });
      });

      return {
        date: entry.timestamp,
        ...kpiMap,
      };
    });
  };

  const transformData = (data, graphKeys) => {
    return data?.map((entry) => {
      const kpiMap = {};
      entry?.kpi?.forEach((kpiItem) => {
        graphKeys?.forEach((key) => {
          if (kpiItem.name === key.key) {
            const formattedKey = `widgets.${props.subtype}.${key.key}`;
            kpiMap[formattedKey] = kpiItem.value?.value || kpiItem.value;
          }
        });
      });

      return {
        date: entry.timestamp,
        ...kpiMap,
      };
    });
  };

  const transformedData = transformData(
    detailKpiData ?? detailKpiData,
    graphValues
  );

  const getColors = () => {
    try {
      if (typeof props.colors[0] === 'string') {
        return props.colors?.[0].startsWith('#')
          ? props.colors
          : props.colors.map((color) => ThemeStore?.colors[color]);
      }
      return props.colors.map(
        (color) => ThemeStore?.colors[color?.color] + (color?.colorShade || '')
      );
    } catch (err) {
      return [...Array(props.buttons?.length || 0)].map(
        (_el, i) => `#000000${(i + 1) * 10}`
      );
    }
  };

  const getGraphButtons = (keys) => {
    return keys?.map((key) => `widgets.${props.subtype}.${key.key}`);
  };

  const getUnit = () => {
    const key =
      (typeof (graphValues as any)?.[0] === 'string'
        ? (graphValues as any)?.[0]
        : (graphValues as any)?.[0]?.key) || null;

    if (!key) {
      // console.warn('no key found');
      return '';
    }
    return findApiUnit(key);
  };

  const getGraphButtonsAreaChart = (keys) => {
    const graphButtons = keys?.map((key) => {
      return `widgets.${props.subtype}.${key}`;
    });

    return graphButtons;
  };

  const getTotalValue = (data) => {
    if (!props.totalValue) {
      return null;
    }
    return data?.map((entry) => {
      const kpiItem = entry.kpi.find((kpiItem) => kpiItem.name === props.totalValue);
      return {
        date: entry.timestamp,
        totalValue: kpiItem?.value,
        unit: kpiItem?.unit,
      };
    });
  };
  const totalValue = getTotalValue(detailKpiData ?? detailKpiData);

  const ref = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);

  useEffect(() => {
    if (!ref.current) return;

    const updateWidth = () => {
      if (ref.current) {
        setContainerWidth(ref.current.offsetWidth - 50);
      }
    };

    // Run updateWidth initially to get the first size
    updateWidth();

    // Use ResizeObserver to listen for changes
    const observer = new ResizeObserver(() => updateWidth());
    observer.observe(ref.current);

    // Cleanup function to disconnect observer
    return () => {
      observer.disconnect();
    };
  }, [ref.current]);

  const getGraph = () => {
    switch (props.subtype) {
      case 'consumptionStackBar':
      case 'productionStackBar':
      case 'savingsStackbar':
      case 'savingsStackBar':
        return (
          <Stackbar
            _data={transformedData}
            originalData={transformedData}
            graphKeys={props.graphKeys.filter(el => graphValues?.some((datum: any) => datum?.key === (typeof el === 'string' ? el : el?.key)))}
            title={props.title}
            unit={getUnit()}
            subtype={props.subtype}
            totalValue={totalValue ?? null}
            isLoading={LOADING_STATE === 'PENDING'}
            isLoadingError={LOADING_STATE === 'ERROR'}
          />
        );
      case 'areachart':
      case 'areaChart':
        return (
          <NewAreaChart
            colors={getColors()}
            title={props.title}
            data={getTranformDataAreachart(detailKpiData, props.graphKeys)}
            keys={getGraphButtonsAreaChart(props.graphKeys)}
            unit={getUnit()}
            containerWidth={containerWidth}
          />
        );
      case 'batteryChartMobile':
        return <BatteryChart {...props} />;
      default:
        return (
          <Stackbar
            _data={transformedData}
            title={props.title}
            unit={getUnit()}
            graphKeys={props.graphKeys}
            subtype={props.subtype}
            totalValue={totalValue ?? null}
          />
        );
    }
  };

  return (
    <div
      className={`${styles.GraphContainer} ${
        props.gradientBorder && styles.GraphContainer__GradientBorder
      } graph-container`}
      style={{ position: 'relative', height: '100%' }}
      ref={ref}
    >
      <div
        className={
          ENV?.VITE_APP_IS_WEB === 'true'
            ? styles.GraphContainer__Web
            : styles.GraphContainer
        }
      >
        {LOADING_STATE === 'PENDING' && <Shimmer />}
        {LOADING_STATE === 'PENDING' && (!transformedData || transformedData?.length === 0) ? (
          // (LOADING_STATE !== 'PENDING' && detailKpiData?.length !== 0)
          <ChartSkeleton
            title={props.title}
            height={ENV?.VITE_APP_IS_WEB === 'true' ? 435 : 400}
          />
        ) : (
          <div className={styles.Graph}>{getGraph()}</div>
        )}{' '}
      </div>
    </div>
  );
});

export default GraphContainer;
